import { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import SaveIcon from "@mui/icons-material/Save";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";

import { Translate } from "@/components/Application/Translate";
import { UserGroupSelect } from "@/components/MaterialHandling/UserGroupSelect";
import { useSaveChecklistTitle } from "@/components/Procedures/API/ManualChecklistCacheMutations";
import { ChecklistProcedureType } from "@/components/Procedures/Pages/ProcedurePage";

import { GroupItem } from "@/model/Application/GroupItem";
import { DocumentImportPlan } from "@/model/Procedure/DocumentImportPlan";
import { DocumentMetadata } from "@/model/Procedure/DocumentMetadata";
import { UserContext } from "@/model/User/UserContext";

import { useMutateUpdateTargetTime } from "@/service/GroupMutations";
import { useFetchUserGroups } from "@/service/MaterialHandlingQueries";
import { useFetchImportPlan } from "@/service/NodeQueries";
import {
	ImportPlanFromWizard,
	useConvertToManualChecklist,
} from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

/* ------------------------------ Chapter Row ------------------------------ */

interface IShowDocumentRow {
	row: DocumentMetadata;
	onChange: (key: string, body: boolean, setactive: boolean) => void;
}

export const ShowDocumentRow: FC<IShowDocumentRow> = ({ row, onChange }) => {
	const [headerUsed, setHeaderUsed] = useState<boolean>(row.SelectedHeader);
	const [bodyUsed, setBodyUsed] = useState<boolean>(row.SelectedBody);

	const onChangeHeader = (state: boolean) => {
		setHeaderUsed(state);
		onChange(row.LinkCode, false, state);
	};

	const onChangeBody = (state: boolean) => {
		setBodyUsed(state);
		onChange(row.LinkCode, true, state);
	};

	return (
		<TableRow hover key={row.LinkCode}>
			<TableCell width={96} sx={{ whiteSpace: "nowrap" }}>
				{row.ChapterNr}
			</TableCell>
			<TableCell>
				{row.FieldType > 0 ? (
					<Typography variant="body2">Table {row.ChapterNr}</Typography>
				) : (
					<Typography variant="body2">{row.ChapterName}</Typography>
				)}
			</TableCell>
			<TableCell align="center" width={140}>
				{row.FieldType === 0 ? (
					<FormControlLabel
						control={
							<Checkbox
								color="info"
								checked={headerUsed}
								onChange={(e) => onChangeHeader(e.target.checked)}
							/>
						}
						label=""
					/>
				) : (
					<Typography variant="caption" color="text.secondary">
						—
					</Typography>
				)}
			</TableCell>
			<TableCell align="center" width={140}>
				<FormControlLabel
					control={
						<Checkbox
							color="info"
							checked={bodyUsed}
							onChange={(e) => onChangeBody(e.target.checked)}
						/>
					}
					label=""
				/>
			</TableCell>
		</TableRow>
	);
};

/* ------------------------------- Main Page ------------------------------- */

interface ImportedChecklistEditorContainerProps {
	editMode: boolean | undefined;
	activeSchemeId: number | undefined;
	checklistProcedureType: ChecklistProcedureType;
	nodeId: number | undefined;
}

export const ImportedChecklistEditorContainer: FC<
	ImportedChecklistEditorContainerProps
> = (props) => {
	const user = useContext(UserContext);

	const [doSave, setDoSave] = useState<boolean>(false);
	const [checklistName, setChecklistName] = useState<string | undefined>("");
	const [returnChecklist, setReturnChecklist] = useState<DocumentImportPlan>();
	const [usedBody, setUsedBody] = useState<Array<string>>();
	const [usedHeader, setUsedHeader] = useState<Array<string>>();
	const [redirectToVerify, setRedirectToVerify] = useState(false);
	const [redirectToPage, setRedirectToPage] = useState(false);
	const [procedureId, setProcedureId] = useState<number>(0);
	const [nodeSchemeId, setNodeSchemeId] = useState<number>(0);
	const [schemeId, setSchemeId] = useState<number>(0);

	// Target time fields
	const [hours, setHours] = useState("00");
	const [minutes, setMinutes] = useState("00");
	const [seconds, setSeconds] = useState("00");
	const targetCompletionTime = `${hours}:${minutes}:${seconds}`;

	const [selectedGroupId, setSelectedGroupId] = useState<number>(-1);
	const defaultGroup: GroupItem = { Id: -1, Name: "----------" };
	const [userGroups, setUserGroups] = useState<Array<GroupItem>>([
		defaultGroup,
	]);

	const { mutateAsync: saveChecklistTitle } = useSaveChecklistTitle();

	const { data: checklist } = useFetchImportPlan(
		Boolean(props.editMode),
		props.nodeId,
		props.activeSchemeId,
	);

	const { data: UserGroups } = useFetchUserGroups();
	const updateTargetTime = useMutateUpdateTargetTime();
	const importPlanFromWizard = ImportPlanFromWizard();

	const handleSubmitTime = async () => {
		try {
			await updateTargetTime({
				request: { value: targetCompletionTime },
				ChecklistId: parseInt(checklist?.SchemeId),
			});
		} catch (e) {
			console.error(e);
		}
	};

	const handleSaveChecklistTitle = (title: string) => {
		setChecklistName(title);
		saveChecklistTitle(title);
	};

	const handleSave = async () => {
		setDoSave(true);
		if (returnChecklist) {
			const request = {
				...returnChecklist,
				OrganisationId: user!.CompanyId,
				UserId: user!.Id,
			};
			const response = await importPlanFromWizard(request);
			if (
				response.ProcedureId > 0 &&
				(response.NodeSchemeId > 0 || response.SchemeId > 0)
			) {
				setProcedureId(response.ProcedureId);
				setNodeSchemeId(response.NodeSchemeId);
				setSchemeId(response.SchemeId);
				if (response.IsNewPage) {
					setRedirectToPage(true);
				} else {
					setRedirectToVerify(true);
				}
			}
		}
		setDoSave(false);
	};

	useEffect(() => {
		// Clear all on context change
		setChecklistName("");
		setReturnChecklist(undefined);
		setUsedHeader([]);
		setUsedBody([]);
		setSelectedGroupId(0);
	}, [props.editMode, props.activeSchemeId]);

	useEffect(() => {
		if (checklist && UserGroups) {
			setChecklistName(checklist.SchemeName);
			setReturnChecklist(checklist);
			setSelectedGroupId(checklist.GroupId);

			const tempheader: string[] = [];
			const tempbody: string[] = [];

			checklist.Chapters?.forEach((chapter) => {
				if (chapter.IsUsedHeader) tempheader.push(chapter.LinkCode);
				if (chapter.IsUsedBody) tempbody.push(chapter.LinkCode);
			});

			setUsedHeader(tempheader);
			setUsedBody(tempbody);

			setUserGroups([defaultGroup, ...UserGroups.List]);
		}
	}, [checklist, UserGroups, props.activeSchemeId, props.editMode]);

	const updatedGroupId = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedGroupId(parseInt(e.currentTarget.value));
	};

	if (!props.editMode) {
		if (redirectToPage) {
			setChecklistName("");
			setProcedureId(0);
			setNodeSchemeId(0);
			setRedirectToPage(false);
		}
		return <></>;
	}

	if (redirectToVerify) {
		return (
			<Navigate
				to={{
					pathname: `/procedure/${procedureId}/node/${nodeSchemeId}/CompareVersions`,
				}}
			/>
		);
	}
	if (redirectToPage) {
		return (
			<Navigate
				to={{ pathname: `/procedure/${procedureId}/checklist/${schemeId}` }}
			/>
		);
	}

	const onChangeUse = (key: string, isbody: boolean, state: boolean) => {
		if (isbody) {
			if (usedBody) {
				if (!state) {
					setUsedBody(usedBody.filter((id) => id !== key));
				} else if (usedBody.indexOf(key) < 0) {
					setUsedBody([...usedBody, key]);
				}
			} else {
				setUsedHeader([key]);
			}
		} else {
			if (usedHeader) {
				if (!state) {
					setUsedHeader(usedHeader.filter((id) => id !== key));
				} else if (usedHeader.indexOf(key) < 0) {
					setUsedHeader([...usedHeader, key]);
				}
			} else {
				setUsedBody([key]);
			}
		}
	};

	const checkSave = () => {
		if (!checklist) return;

		let importplan = checklist;
		let errorMsg = "";

		importplan.GroupId = selectedGroupId;

		if (checklistName && checklistName.length > 0) {
			importplan.SchemeName = checklistName;
		} else {
			errorMsg = "Name cannot be empty";
		}

		if (errorMsg === "") {
			if (usedBody || usedHeader) {
				checklist.Chapters.forEach((chapter) => {
					chapter.SelectedBody = !!usedBody?.includes(chapter.LinkCode);
					chapter.SelectedHeader = !!usedHeader?.includes(chapter.LinkCode);
				});
			} else {
				errorMsg = "Nothing selected";
			}
		}

		if (errorMsg === "") {
			handleSubmitTime();
			setReturnChecklist(importplan);
			handleSave();
		}
	};

	/* ------------------------------- UI LAYOUT ------------------------------ */

	return (
		<Box sx={{ p: { xs: 1, md: 2 } }}>
			<Stack spacing={2}>
				{/* Title + Group */}
				<Card variant="outlined" sx={{ borderRadius: 2 }}>
					<CardHeader
						title={
							<Typography variant="h6" fontWeight={700}>
								<Translate String="EditChecklistWizard.Title">
									Checklist
								</Translate>
							</Typography>
						}
						subheader={
							<Typography variant="body2" color="text.secondary">
								<Translate String="EditChecklistWizard.Subtitle">
									Set title, choose who checks steps, and configure chapters.
								</Translate>
							</Typography>
						}
					/>
					<CardContent>
						<Grid container spacing={2}>
							<Grid item xs={12} md={12}>
								<TextField
									size="small"
									label="Title"
									fullWidth
									value={checklistName ?? ""}
									onChange={(e) => handleSaveChecklistTitle(e.target.value)}
								/>
							</Grid>
							<Grid item xs={12} md={12} sx={{ mb: 3 }}>
								<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
									<Translate String="EditChecklistWizard.Usergroup_select">
										User group who check steps.
									</Translate>
								</Typography>
								<UserGroupSelect
									userGroup={userGroups}
									OnChange={updatedGroupId}
									Selected={selectedGroupId}
								/>
							</Grid>
						</Grid>
					</CardContent>
				</Card>

				{/* Chapters table */}
				<Card variant="outlined" sx={{ borderRadius: 2 }}>
					<CardHeader
						title={
							<Typography variant="h6" fontWeight={700}>
								<Translate String="EditChecklistWizard.Chapters">
									Chapters
								</Translate>
							</Typography>
						}
						subheader={
							<Typography variant="body2" color="text.secondary">
								<Translate String="EditChecklistWizard.ChaptersHelp">
									Pick which sections to include as header and/or body.
								</Translate>
							</Typography>
						}
					/>
					<CardContent>
						<TableContainer
							component={Paper}
							variant="outlined"
							sx={{ borderRadius: 2 }}
						>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow sx={{ "& th": { fontWeight: 700 } }}>
										<TableCell width={96}>#</TableCell>
										<TableCell>Name</TableCell>
										<TableCell align="center" width={140}>
											Header
										</TableCell>
										<TableCell align="center" width={140}>
											Body
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{checklist?.Chapters?.map((chapter) => (
										<ShowDocumentRow
											key={chapter.LinkCode}
											row={chapter}
											onChange={onChangeUse}
										/>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</CardContent>
				</Card>

				{/* Target time + Actions */}
				<Card variant="outlined" sx={{ borderRadius: 2 }}>
					<CardContent>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} md={3} sx={{ mb: 3 }}>
								<Typography variant="subtitle1" fontWeight={700}>
									Set target time
								</Typography>
								<Typography variant="caption" color="text.secondary">
									HH : MM : SS
								</Typography>
							</Grid>
							<Grid item xs={4} md={2}>
								<TextField
									size="small"
									label="Hours"
									fullWidth
									value={hours}
									onChange={(e) => setHours(formatTime(e.target.value))}
								/>
							</Grid>
							<Grid item xs={4} md={2}>
								<TextField
									size="small"
									label="Minutes"
									fullWidth
									value={minutes}
									onChange={(e) => setMinutes(formatTime(e.target.value))}
								/>
							</Grid>
							<Grid item xs={4} md={2}>
								<TextField
									size="small"
									label="Seconds"
									fullWidth
									value={seconds}
									onChange={(e) => setSeconds(formatTime(e.target.value))}
								/>
							</Grid>
							<Grid item xs={12} md={3}>
								<Stack
									direction="row"
									justifyContent={{ xs: "stretch", md: "flex-end" }}
								>
									<Button
										variant="contained"
										color="info"
										startIcon={
											doSave ? (
												<CircularProgress size={18} color="inherit" />
											) : (
												<SaveIcon />
											)
										}
										onClick={checkSave}
										disabled={doSave}
									>
										{doSave ? "Saving..." : "Save"}
									</Button>
								</Stack>
							</Grid>
						</Grid>
					</CardContent>
				</Card>

				{/* Convert sections: not added yet */}
			</Stack>
		</Box>
	);
};

/* ----------------------- Convert to Manual section ----------------------- */

interface ChecklistConvertToManualProps {
	activeSchemeId: number | undefined;
}

const ChecklistConvertToManual: FC<ChecklistConvertToManualProps> = ({
	activeSchemeId,
}) => {
	const { id } = useParams<RouteParams>();
	const [disabled, setDisabled] = useState<boolean>(true);
	const [redirectTo, setRedirectTo] = useState<number | string | undefined>();

	const ConvertToManual = useConvertToManualChecklist();

	const toggleDisabled = () => {
		if (activeSchemeId) setDisabled((d) => !d);
	};

	const ConvertFn = async () => {
		if (activeSchemeId) {
			const result = await ConvertToManual({
				SchemeId: activeSchemeId,
				ProcedureId: id,
			});
			if (result?.Success) setRedirectTo(result?.ProcedureId);
		}
	};

	if (redirectTo) {
		const pathname = `/procedure/${redirectTo}`;
		setRedirectTo(undefined);
		setDisabled(true);
		return <Navigate to={{ pathname }} />;
	}

	return (
		<Alert severity="warning" sx={{ borderRadius: 2 }}>
			<Typography variant="h6" fontWeight={700}>
				<Translate String="Convert.To.Manual.Warning.Text">
					Convert to Manual
				</Translate>
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
				<Translate String="Convert.To.Manual.Caution.Text">
					Caution! This will convert this checklist to a manual checklist.
				</Translate>
			</Typography>

			<Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={!disabled}
							onChange={toggleDisabled}
							color="warning"
						/>
					}
					label={
						<Typography variant="body2">I understand the change</Typography>
					}
				/>
				<Button
					variant="contained"
					color="warning"
					disabled={disabled}
					onClick={ConvertFn}
				>
					<Translate String="Convert.To.Manual.Button">
						Convert to Manual
					</Translate>
				</Button>
			</Stack>
		</Alert>
	);
};

/* --------------------------------- Utils -------------------------------- */

export const formatTime = (value: string) => {
	const numericValue = Math.max(0, parseInt(value, 10));
	if (isNaN(numericValue) || numericValue < 10) {
		return `0${isNaN(numericValue) ? 0 : numericValue}`;
	} else if (numericValue > 59) {
		return "59";
	} else {
		return `${numericValue}`;
	}
};
